import type { AdminRole } from '../lib/types.js';

interface RoleSelectorProps {
  roles: AdminRole[];
  selectedRoles: string[];
  onSelectedRolesChange: (roles: string[]) => void;
  isLoading: boolean;
  isDisabled: boolean;
  /** Viewing your own page: tenant administration is locked, mirroring the
   *  server guard. Removing it from yourself is a lockout with no way back. */
  isSelf?: boolean;
}

/**
 * The admin-role picker. Deliberately the same checkbox shape as ScopeSelector
 * — an operator granting access shouldn't have to learn two interactions — but
 * without its family sections, audience filter, or search: there are a handful
 * of roles, not thirty scopes, and sections over four rows is furniture.
 *
 * A role the tenant hasn't bought stays VISIBLE and disabled, with the reason.
 * Hiding it leaves someone hunting for a role that exists in the product and
 * not in their portal.
 */
export const RoleSelector = ({
  roles,
  selectedRoles,
  onSelectedRolesChange,
  isLoading,
  isDisabled,
  isSelf = false,
}: RoleSelectorProps) => {
  const selected = new Set(selectedRoles);

  const toggle = (key: string, checked: boolean) => {
    onSelectedRolesChange(
      checked ? [...selectedRoles, key] : selectedRoles.filter((r) => r !== key),
    );
  };

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <span className="text-sm font-medium text-ink">Roles</span>
        <span className="text-xs text-ink-subtle">
          {selected.size} of {roles.length} selected
        </span>
      </div>

      {isLoading ? (
        <div className="flex h-32 items-center justify-center rounded border border-outline text-sm text-ink-muted">
          Loading roles…
        </div>
      ) : roles.length === 0 ? (
        <div className="flex h-32 items-center justify-center rounded border border-dashed border-outline px-6 text-center text-sm text-ink-muted">
          No admin roles are defined.
        </div>
      ) : (
        <div className="space-y-0.5 rounded border border-outline p-2">
          {roles.map((role) => (
            <label
              key={role.key}
              className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                role.available ? 'cursor-pointer hover:bg-surface-hover' : 'opacity-60'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(role.key)}
                onChange={(e) => toggle(role.key, e.target.checked)}
                disabled={
                  isDisabled ||
                  !role.available ||
                  (isSelf && (role.key === 'TENANT_ADMIN' || role.key === 'SUPER_ADMIN'))
                }
                className="mt-0.5 h-4 w-4 flex-none accent-sc"
              />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                  <span className="text-sm font-medium text-ink">{role.displayName}</span>
                  <span className="font-mono text-[11px] text-ink-subtle">{role.key}</span>
                </span>
                <span className="mt-0.5 block text-xs text-ink-muted">{role.description}</span>
                {role.grants.length > 0 && (
                  <span className="mt-1 block text-xs text-ink-subtle">
                    Unlocks: {role.grants.join(' · ')}
                  </span>
                )}
                {isSelf && (role.key === 'TENANT_ADMIN' || role.key === 'SUPER_ADMIN') ? (
                  <span className="mt-1 block text-xs text-warning">
                    You can&apos;t change your own administration — ask another admin.
                  </span>
                ) : !role.grantableInApp ? (
                  <span className="mt-1 block text-xs text-warning">
                    Set in the database only — it cannot be granted from the portal.
                  </span>
                ) : (
                  !role.available && (
                    <span className="mt-1 block text-xs text-warning">
                      Needs the {role.requiresModule} module — a super admin enables it on the
                      Modules page.
                    </span>
                  )
                )}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
